import { useState, useEffect, useCallback, useMemo } from 'react'
import { View, Text, StyleSheet } from 'react-native'
import { TouchableOpacity } from 'react-native'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { showConfirm } from '../../lib/platform'
import { colors, fonts, radius } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { Card, SubHeader, Msg, RowBtn } from '../../components/Leagues/ui'
import Ionicons from '@expo/vector-icons/Ionicons'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import GradientButton from '../../components/GradientButton'
import Sheet from './Sheet'

export default function SezioneMembri({ leagueId, isAdmin, currentUserId, memberCount, onMembersChange }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const [open, setOpen] = useState(false)
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [msg, setMsg] = useState(null)

  const loadMembers = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.rpc('fn_league_members', { p_id_league: leagueId })
    setMembers(data ?? [])
    setLoading(false)
  }, [leagueId])

  useEffect(() => { if (open) loadMembers() }, [open, loadMembers])

  const handleRemove = async (targetId, name) => {
    if (!await showConfirm(t('league.confirm_remove', { name }))) return
    await supabase.rpc('fn_remove_member', { p_caller: currentUserId, p_id_league: leagueId, p_target_user: targetId })
    setMsg({ type: 'success', text: t('league.member_removed', { name }) })
    loadMembers(); onMembersChange?.()
  }

  const handleToggleAdmin = async (targetId, name, isLeagueAdmin) => {
    if (!await showConfirm(t(isLeagueAdmin ? 'league.confirm_demote' : 'league.confirm_promote', { name }))) return
    await supabase.rpc('fn_set_league_admin', { p_caller: currentUserId, p_id_league: leagueId, p_target_user: targetId, p_is_admin: !isLeagueAdmin })
    loadMembers()
  }

  const handleLeave = async () => {
    if (!await showConfirm(t('profile.league_leave_confirm', { name: '' }))) return
    setLeaving(true)
    const { data, error } = await supabase.rpc('fn_leave_league', { p_id_user: currentUserId, p_id_league: leagueId })
    if (!error && data?.[0]?.success) onMembersChange?.('leave')
    setLeaving(false)
  }

  return (
    <>
      <Card>
        <View style={styles.cardRow}>
          <View style={{ flex: 1 }}>
            <SubHeader icon={<Ionicons name="people-outline" size={16} color={tc.rosaText} />}>{t('league.members_section')}</SubHeader>
            <Text style={[styles.cardDesc, { color: tc.textMuted }]}>{t('league.member_count', { count: memberCount })}</Text>
          </View>
          <GradientButton onPress={() => setOpen(true)} size="sm">
            {isAdmin ? t('league.manage') : t('league.view')}
          </GradientButton>
        </View>
      </Card>
      <Sheet visible={open} title={isAdmin ? t('league.members_title_admin') : t('league.members_title_user')} onClose={() => { setOpen(false); setMsg(null) }}
        footer={!isAdmin ? (
          <TouchableOpacity style={[styles.leaveBtn, { borderColor: tc.border }]} onPress={handleLeave} disabled={leaving} accessibilityRole="button" accessibilityLabel={t('league.leave')} accessibilityState={{ disabled: leaving }}>
            <Text style={[styles.leaveBtnText, { color: tc.rosaText }]}>{leaving ? t('league.leaving') : t('league.leave')}</Text>
          </TouchableOpacity>
        ) : null}
      >
        {msg && <Msg type={msg.type} text={msg.text} />}
        {loading ? <Text style={{ color: tc.textMuted, fontSize: 13, textAlign: 'center', padding: 20 }}>{t('league.loading')}</Text> : (
          <View style={{ gap: 8 }}>
            {members.map(m => (
              <View key={m.id_user} style={[styles.memberRow, { backgroundColor: m.id_user === currentUserId ? tc.rosaSubtle : tc.bgCard2, borderColor: m.id_user === currentUserId ? tc.rosaBorder : tc.border }]}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Text style={[styles.memberName, { color: tc.text }]}>{m.display_name ?? '—'}</Text>
                    {m.id_user === currentUserId && <View style={styles.tuBadge}><Text style={styles.tuBadgeText}>{t('common.tu')}</Text></View>}
                  </View>
                  {m.is_league_admin && <Text style={styles.adminBadge}>{t('league.admin_badge')}</Text>}
                </View>
                {isAdmin && m.id_user !== currentUserId && (
                  <View style={{ flexDirection: 'row', gap: 6 }}>
                    <RowBtn onPress={() => handleToggleAdmin(m.id_user, m.display_name, m.is_league_admin)} variant="warning">
                      {m.is_league_admin ? t('league.demote_admin') : t('league.promote_admin')}
                    </RowBtn>
                    {!m.is_league_admin && (
                      <RowBtn onPress={() => handleRemove(m.id_user, m.display_name)} variant="danger">{t('league.remove')}</RowBtn>
                    )}
                  </View>
                )}
              </View>
            ))}
          </View>
        )}
      </Sheet>
    </>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  cardRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  cardDesc: { fontSize: 12, marginTop: -8 },
  memberRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 12, paddingHorizontal: 14, borderRadius: 12, borderWidth: 1 },
  memberName: { fontSize: 14, fontWeight: '600' },
  tuBadge: { backgroundColor: tc.rosaBorder, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 },
  tuBadgeText: { fontSize: 11, color: tc.rosaText, fontFamily: fonts.condensedBold },
  adminBadge: { fontSize: 11, color: tc.warning, fontWeight: '700', marginTop: 3 },
  leaveBtn: { borderWidth: 1, borderRadius: 10, padding: 12, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  leaveBtnText: { fontFamily: fonts.condensedBold, fontSize: 14 },
})